//@ts-ignore
import io, { Socket } from 'socket.io-client';
import { eventChannel } from 'redux-saga';
import { fork, take, call, put, cancel, takeLatest, select } from 'redux-saga/effects';
import {
    sendMessage,
    sendMessageSuccess,
    retrieveUserRooms,
    userTyping,
    joinChatRoom,
    typingStatusSocket,
    userHasReadMessage,
    joinAllChatRooms,
    newContent,
    setNewContentAvailable,
    getMessagesForSelectedRoom,
    getMyRoomsSuccess,
    leaveRoomAction,
    updateUnreadMessageCount,
    sendMessageFail,
    updateMessageSuccess,
    deleteMessageSuccess,
    reactToMessageSuccess,
    updateMessage,
    deleteMessage,
    reactToMessage,
} from './actions';

import { SOCKET } from '../../configurations/api/url';
import type { IMessagePayload } from './types';
import { SOCKET_REQUESTS } from './types';
import { retrieveJwt } from '../authentication';
import { types, NewContentType } from './types';
import { setRoomMessages } from './actions';
import { getJWtDetails } from '../../utils/AppUtils';
import { disconnect, setOnlineUsers, setOfflineUsers, setOnlineStatus } from '../live/action';
import { setSocketError } from '../error/actions';
import { logoutSuccess } from '../authentication/action';
import { CAMPAIGN_THREAD_SOCKET } from '../buzzbridge/campaignThreadSocket.events';
import {
    campaignThreadAttachmentProcessing,
    campaignThreadMessageDeleted,
    campaignThreadMessageNew,
    campaignThreadMessageUpdated,
    campaignThreadSocketError,
    campaignThreadSocketJoin,
    campaignThreadSocketLeave,
    campaignThreadSocketTyping,
    campaignThreadTypingReceived,
    campaignThreadUnreadUpdated,
} from '../buzzbridge/actions';
import {
    brandOpsNewClarificationReceived,
    brandOpsContractSignedReceived,
    brandOpsContractDeclinedReceived,
    brandOpsNewDmReceived,
} from '../brandOps/actions';
import {
    CAMPAIGN_THREAD_SOCKET_ALIASES,
    normalizeCampaignThreadAttachmentProcessingPayload,
    normalizeCampaignThreadDeletedPayload,
    normalizeCampaignThreadErrorPayload,
    normalizeCampaignThreadMessagePayload,
    normalizeCampaignThreadTypingPayload,
    normalizeCampaignThreadUnreadPayload,
    registerCampaignThreadSocketAliases,
} from '../../utils/campaignThreadSocketPayload';
import type { RootState } from '../root-reducer';

function connect(jwt: any, userID: string) {
    const socket = io(SOCKET, {
        forceNew: true,
        transports: ['websocket', 'polling'],
        transportOptions: {
            polling: {
                extraHeaders: {
                    Authorization: `Bearer ${jwt}`,
                    'X-User': userID,
                },
            },
            websocket: {
                extraHeaders: {
                    Authorization: `Bearer ${jwt}`,
                    'X-User': userID,
                },
            },
        },
        auth: {
            token: `Bearer ${jwt}`,
            userID,
        },
    });
    socket.emit(SOCKET_REQUESTS.ONLINE, { status: 'online' });
    return new Promise(resolve => {
        socket.on('connect', () => {
            resolve(socket);
        });
    });
}

function subscribe(socket: Socket) {
    return eventChannel(emit => {
        /** The below shoudl get all messages and we then sort them by RoomID */
        socket.on(SOCKET_REQUESTS.GET_ROOMS, ({ rooms, pagination }) => {
            /** Dispatch the success here */
            emit(getMyRoomsSuccess({ rooms, pagination }));
        });

        socket.on(SOCKET_REQUESTS.MESSAGE_EDITED, ({ updatedMessage, currentRoom }) => {
            /** Dispatch the success here */
            emit(updateMessageSuccess({ updatedMessage, currentRoom }));
        });

        socket.on(SOCKET_REQUESTS.MESSAGE_DELETED, ({ messageId, currentRoom }) => {
            /** Dispatch the success here */
            emit(deleteMessageSuccess({ messageId, currentRoom }));
        });

        socket.on(SOCKET_REQUESTS.REACTION_UPDATED, ({ updatedMessage, room, action }) => {
            /** Dispatch the success here */
            emit(reactToMessageSuccess({ updatedMessage, room, action }));
        });

        socket.on(SOCKET_REQUESTS.NEW_MESSAGE, ({ newMessage, currentRoom, uniqueSocketID }) => {
            const payload = newMessage;
            payload.newRoom = currentRoom;
            payload.uniqueSocketID = uniqueSocketID;
            emit(sendMessageSuccess(payload));
        });

        socket.on(
            SOCKET_REQUESTS.FAILED_MESSAGE,
            ({ payload, uniqueSocketID }: { payload: IMessagePayload; uniqueSocketID: string }) => {
                emit(
                    sendMessageFail({
                        payload,
                        uniqueSocketID,
                    }),
                );
            },
        );
        socket.on(SOCKET_REQUESTS.DISCONNECT, e => {
            // TODO: handle
        });

        socket.on(SOCKET_REQUESTS.USER_TYPING, ({ status, roomID, userId }) => {
            // emit an action
            emit(userTyping({ status, roomID, userId }));
        });

        socket.on(SOCKET_REQUESTS.ROOM_MESSAGES, ({ messages, pagination, participants }) => {
            emit(setRoomMessages({ messages, pagination, participants }));
        });

        socket.on(SOCKET_REQUESTS.SET_ONLINE_USERS, ({ user }) => {
            emit(setOnlineUsers(user));
        });

        socket.on(SOCKET_REQUESTS.SET_OFFLINE_USERS, ({ user }) => {
            emit(setOfflineUsers(user));
        });

        socket.on(SOCKET_REQUESTS.NEW_CONTENT_AVAILABLE, ({ content_type }: { content_type: NewContentType }) => {
            emit(setNewContentAvailable(content_type));
        });

        socket.on(
            SOCKET_REQUESTS.UNREAD_COUNT_UPDATE,
            ({ unreadCount, roomID, readBy }: { unreadCount: number; roomID: string; readBy: string }) => {
                emit(updateUnreadMessageCount({ unreadCount, roomID, readBy }));
            },
        );

        socket.on(
            'brandOpsContractClarification',
            (data: { contract_id: string; clarification_id: string; manager_email: string; message: string }) => {
                emit(brandOpsNewClarificationReceived(data));
            },
        );

        socket.on(
            'brandOpsContractSigned',
            (data: {
                contract_id: string;
                manager_email: string;
                manager_name: string;
                requires_approval: boolean;
            }) => {
                emit(brandOpsContractSignedReceived(data));
            },
        );

        socket.on('brandOpsContractDeclined', (data: { manager_email: string; reason: string | null }) => {
            emit(brandOpsContractDeclinedReceived(data));
        });

        socket.on(
            'brandOpsNewDm',
            (data: { ig_user_id: string; sender_id: string; message_id: string; text: string; timestamp: number }) => {
                emit(brandOpsNewDmReceived(data));
            },
        );

        socket.on(SOCKET_REQUESTS.ERROR, ({ message, status }: { message: string; status: number }) => {
            emit(
                setSocketError({
                    data: {
                        message: message,
                        status: 'failed',
                    },
                    status: status,
                    statusText: message,
                }),
            );
        });

        registerCampaignThreadSocketAliases(
            socket,
            CAMPAIGN_THREAD_SOCKET.MESSAGE_NEW,
            CAMPAIGN_THREAD_SOCKET_ALIASES.messageNew,
            payload => {
                const normalized = normalizeCampaignThreadMessagePayload(payload);
                if (normalized) emit(campaignThreadMessageNew(normalized));
            },
        );

        registerCampaignThreadSocketAliases(
            socket,
            CAMPAIGN_THREAD_SOCKET.MESSAGE_UPDATED,
            CAMPAIGN_THREAD_SOCKET_ALIASES.messageUpdated,
            payload => {
                const normalized = normalizeCampaignThreadMessagePayload(payload);
                if (normalized) emit(campaignThreadMessageUpdated(normalized));
            },
        );

        registerCampaignThreadSocketAliases(
            socket,
            CAMPAIGN_THREAD_SOCKET.MESSAGE_DELETED,
            CAMPAIGN_THREAD_SOCKET_ALIASES.messageDeleted,
            payload => {
                const normalized = normalizeCampaignThreadDeletedPayload(payload);
                if (normalized) emit(campaignThreadMessageDeleted(normalized));
            },
        );

        registerCampaignThreadSocketAliases(
            socket,
            CAMPAIGN_THREAD_SOCKET.ATTACHMENT_PROCESSING,
            CAMPAIGN_THREAD_SOCKET_ALIASES.attachmentProcessing,
            payload => {
                const normalized = normalizeCampaignThreadAttachmentProcessingPayload(payload);
                if (normalized) emit(campaignThreadAttachmentProcessing(normalized));
            },
        );

        registerCampaignThreadSocketAliases(
            socket,
            CAMPAIGN_THREAD_SOCKET.UNREAD_UPDATED,
            CAMPAIGN_THREAD_SOCKET_ALIASES.unreadUpdated,
            payload => {
                const normalized = normalizeCampaignThreadUnreadPayload(payload);
                if (normalized) emit(campaignThreadUnreadUpdated(normalized));
            },
        );

        const handleTypingPayload = (payload: unknown) => {
            const normalized = normalizeCampaignThreadTypingPayload(payload);
            if (!normalized) return;
            emit(
                campaignThreadTypingReceived({
                    participantId: normalized.participantId,
                    typing: normalized.typing
                        ? { userId: normalized.userId, displayName: normalized.displayName }
                        : null,
                }),
            );
        };

        registerCampaignThreadSocketAliases(
            socket,
            CAMPAIGN_THREAD_SOCKET.TYPING,
            CAMPAIGN_THREAD_SOCKET_ALIASES.typing,
            handleTypingPayload,
        );

        registerCampaignThreadSocketAliases(
            socket,
            CAMPAIGN_THREAD_SOCKET.ERROR,
            CAMPAIGN_THREAD_SOCKET_ALIASES.error,
            payload => {
                emit(campaignThreadSocketError(normalizeCampaignThreadErrorPayload(payload)));
            },
        );

        return () => {};
    });
}

function* read(socket: Socket): any {
    const channel = yield call(subscribe, socket);
    while (true) {
        const action = yield take(channel);
        yield put(action);
    }
}

function* write(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${sendMessage}`);
        socket.emit('message', payload);
        socket.emit('typingStatus', { status: 'no', roomID: payload.roomID });
    }
}

/** This Actually gets Rooms and not messages */
function* getUserRooms(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${retrieveUserRooms}`);
        /** Dispatch the initial action here */
        socket.emit(SOCKET_REQUESTS.RETRIEVE_ROOMS, payload);
    }
}

function* userHasRead(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${userHasReadMessage}`);
        socket.emit(SOCKET_REQUESTS.USER_HAS_READ, payload);
    }
}

function* joinRoom(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${joinChatRoom}`);
        socket.emit(SOCKET_REQUESTS.JOIN_ROOM, payload);
    }
}

function* leaveRoom(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${leaveRoomAction}`);
        socket.emit(SOCKET_REQUESTS.LEAVE_ROOM, payload);
    }
}

function* joinAllRooms(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${joinAllChatRooms}`);
        socket.emit(SOCKET_REQUESTS.JOIN_ALL_ROOMS, payload);
    }
}

/** Get the messages for a particular room */
function* getRoomMessages(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${getMessagesForSelectedRoom}`);
        socket.emit(SOCKET_REQUESTS.GET_ROOM_MESSAGES, payload);
    }
}

/** Typing status for user */
function* typingStatus(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${typingStatusSocket}`);
        socket.emit(SOCKET_REQUESTS.USER_TYPING, payload);
    }
}

/** Set whether a user is online or offline */
function* setUserOnlineStatus(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${setOnlineStatus}`);
        socket.emit(SOCKET_REQUESTS.ONLINE, { status: payload });
    }
}

function* newContentDispatcher(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${newContent}`);
        socket.emit(SOCKET_REQUESTS.NEW_CONTENT, { content_type: payload });
    }
}

function* updateMessageSocket(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${updateMessage}`);
        socket.emit(SOCKET_REQUESTS.EDIT_MESSAGE, payload);
    }
}

function* deleteMessageSocket(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${deleteMessage}`);
        socket.emit(SOCKET_REQUESTS.DELETE_MESSAGE, payload);
    }
}

function* reactToMessageSocket(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${reactToMessage}`);
        socket.emit(SOCKET_REQUESTS.EDIT_REACTION, payload);
    }
}

function* logoutTrigger(socket: Socket) {
    while (true) {
        yield take(`${logoutSuccess}`);
        socket.emit(SOCKET_REQUESTS.LOGOUT);
    }
}

function* replayCampaignThreadRooms(socket: Socket) {
    const rooms: Record<string, true> | undefined = yield select(
        (state: RootState) => state.buzzbridge.campaignThreadActiveRooms,
    );
    for (const participantId of Object.keys(rooms ?? {})) {
        socket.emit(CAMPAIGN_THREAD_SOCKET.JOIN, { participantId });
    }
}

function* campaignThreadJoinRoom(socket: Socket) {
    yield call(replayCampaignThreadRooms, socket);
    while (true) {
        const { payload } = yield take(`${campaignThreadSocketJoin}`);
        socket.emit(CAMPAIGN_THREAD_SOCKET.JOIN, payload);
    }
}

function* campaignThreadLeaveRoom(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${campaignThreadSocketLeave}`);
        socket.emit(CAMPAIGN_THREAD_SOCKET.LEAVE, payload);
    }
}

function* campaignThreadTypingEmit(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${campaignThreadSocketTyping}`);
        const pid = payload?.participantId?.trim();
        if (!pid) continue;
        /** Idempotent — ensures room membership before typing (fixes race on fast first keystroke). */
        socket.emit(CAMPAIGN_THREAD_SOCKET.JOIN, { participantId: pid });
        socket.emit(CAMPAIGN_THREAD_SOCKET.TYPING, payload);
    }
}

function* handleIO(socket: Socket) {
    yield fork(newContentDispatcher, socket);
    yield fork(setUserOnlineStatus, socket);
    yield fork(read, socket);
    yield fork(write, socket);
    yield fork(getUserRooms, socket);
    yield fork(joinRoom, socket);
    yield fork(leaveRoom, socket);
    yield fork(joinAllRooms, socket);
    yield fork(typingStatus, socket);
    yield fork(userHasRead, socket);
    yield fork(getRoomMessages, socket);
    yield fork(updateMessageSocket, socket);
    yield fork(deleteMessageSocket, socket);
    yield fork(reactToMessageSocket, socket);
    yield fork(logoutTrigger, socket);
    yield fork(campaignThreadJoinRoom, socket);
    yield fork(campaignThreadLeaveRoom, socket);
    yield fork(campaignThreadTypingEmit, socket);
}

function* flow(): any {
    while (true) {
        const jwt = yield retrieveJwt();
        const { isSignedIn, user_id } = getJWtDetails(jwt);
        if (isSignedIn) {
            const socket = yield call(connect, jwt, user_id);
            const task = yield fork(handleIO, socket);
            yield take(`${disconnect}`);
            yield cancel(task);
            socket.emit(SOCKET_REQUESTS.DISCONNECT);
        }
    }
}

function* flowWatcher() {
    yield takeLatest(types.CONNECT, flow);
}

export default function* socketSaga() {
    yield fork(flowWatcher);
}
